/**
 * Receipts → "Send as invoice" (spec §2.4 P1/P2). A paid sale becomes a PAID
 * invoice-receipt; a "Pay later" sale becomes an issued invoice for the
 * customer. Either way the share options open next. The server keeps one
 * invoice per sale, so sending the same sale again opens the same invoice.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InvoiceRepository, { CustomerBalance, InvoiceDoc } from '../../../internals/repository/InvoiceRepository';
import { errorText } from '../detailModel';
import type { EditorCustomer } from '../editorModel';
import { money } from '../format';
import { creditWarning, modeForOrder, toMinor } from '../posModel';
import CustomerPicker from './CustomerPicker';
import ShareDialog from './ShareDialog';
import { Dialog, ErrorBanner } from './VoidModal';

const ERRORS = {
  invalid_state: 'This sale cannot be sent as an invoice. Only completed sales can.',
  order_unmappable: "This sale's amounts do not add up to its total, so it cannot become an invoice.",
  not_found: 'This sale is not in the cloud yet. Sync the POS and try again.',
};

export default function SendAsInvoiceDialog({
  shopId, order, currency = 'LKR', onClose,
}: {
  shopId: string;
  order: any;
  currency?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const mode = modeForOrder(order);
  const [customer, setCustomer] = useState<EditorCustomer | null>(null);
  const [balance, setBalance] = useState<CustomerBalance | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doc, setDoc] = useState<InvoiceDoc | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const orderHasClient = !!order?.clientId;
  // The search opens only on request: focusing it on open would cover the buttons.
  const [picking, setPicking] = useState(modeForOrder(order) === 'ON_ACCOUNT' && !orderHasClient);
  const orderClientId: string | undefined = order?.clientId || undefined;
  const clientId = customer?.clientId || orderClientId;
  const totalMinor = toMinor(order?.totalPrice);

  useEffect(() => {
    setBalance(null);
    if (mode !== 'ON_ACCOUNT' || !clientId) return undefined;
    let live = true;
    InvoiceRepository.customerBalance(shopId, clientId).then((b) => { if (live) setBalance(b); }).catch(() => undefined);
    return () => { live = false; };
  }, [shopId, clientId, mode]);

  const submit = async () => {
    if (busy) return;
    if (customer && !customer.clientId) { setError('Pick a saved customer, or leave it empty.'); return; }
    if (mode === 'ON_ACCOUNT' && !clientId) { setError('A sale on account needs a customer.'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await InvoiceRepository.fromOrder(shopId, order.orderId, mode, customer?.clientId || undefined);
      if (res.creditWarning) setWarning(creditWarning(res.creditWarning, currency));
      setDoc(res.document);
    } catch (e: any) {
      if (e?.code === 'already_invoiced' && e.details?.document) { setDoc(e.details.document); return; }
      setError(errorText(e, ERRORS));
      setBusy(false);
    }
  };

  if (doc && sharing) {
    return <ShareDialog open shopId={shopId} docId={doc.docId} document={doc} onClose={onClose} />;
  }
  if (doc) {
    return (
      <Dialog
        title={`${doc.number ?? 'Invoice'} is ready`}
        sub={doc.displayStatus === 'PAID' ? 'A paid invoice-receipt for this sale.' : `An invoice for ${doc.customer?.name ?? 'the customer'} to pay, due ${doc.dueDate ?? 'on receipt'}.`}
        onClose={onClose}
        footer={(
          <>
            <button type="button" className="nl-btn nl-btn--secondary" onClick={() => navigate(`/invoices/${doc.docId}`)}>Open invoice</button>
            <button type="button" className="nl-btn nl-btn--primary" onClick={() => setSharing(true)}>Share</button>
          </>
        )}
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between rounded-lg border border-borderc px-3.5 py-2.5 text-[13px]">
            <span className="text-text2">{doc.customer?.name}</span>
            <span className="font-semibold text-text1" style={{ fontVariantNumeric: 'tabular-nums' }}>{money(doc.totalMinor, currency)}</span>
          </div>
          {warning && (
            <div role="status" className="rounded-lg border border-warn/40 bg-warn-soft px-3.5 py-2.5 text-[13px]" style={{ color: 'var(--warnFg)' }}>
              {warning}
            </div>
          )}
        </div>
      </Dialog>
    );
  }

  const ref = order?.recieptId || order?.orderId;
  const warn = mode === 'ON_ACCOUNT' ? creditWarning(balance, currency, totalMinor) : null;
  return (
    <Dialog
      title="Send as invoice"
      sub={mode === 'PAID'
        ? `Sale ${ref} was paid at the counter. It becomes a paid invoice-receipt you can share. Nothing is charged again.`
        : `Sale ${ref} was on account. It becomes an issued invoice for the customer to pay.`}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className="nl-btn nl-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="nl-btn nl-btn--primary" onClick={submit} disabled={busy}>
            {busy ? 'Creating…' : 'Create and share'}
          </button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between rounded-lg border border-borderc px-3.5 py-2.5 text-[13px]">
          <span className="text-text2">{mode === 'PAID' ? 'Paid invoice-receipt' : 'Invoice on account'}</span>
          <span className="font-semibold text-text1" style={{ fontVariantNumeric: 'tabular-nums' }}>{money(totalMinor, currency)}</span>
        </div>
        <div className="flex flex-col gap-1.5 text-[13px] font-semibold text-text2">
          {mode === 'PAID' ? 'Customer (optional)' : 'Customer'}
          {picking ? (
            <CustomerPicker shopId={shopId} value={customer} onChange={(c) => { setCustomer(c); setError(null); }} />
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-borderc px-3.5 py-2.5 font-normal">
              <span className="text-text1">
                {orderClientId ? 'The customer on the sale' : 'Walk-in customer'}
              </span>
              <button type="button" className="nl-btn nl-btn--xs nl-btn--secondary" onClick={() => setPicking(true)}>
                {orderClientId ? 'Change' : 'Add a customer'}
              </button>
            </div>
          )}
        </div>
        {warn && (
          <div role="status" className="rounded-lg border border-warn/40 bg-warn-soft px-3.5 py-2.5 text-[13px]" style={{ color: 'var(--warnFg)' }}>
            {warn} You can still send it.
          </div>
        )}
        <ErrorBanner text={error} />
      </div>
    </Dialog>
  );
}
